import { FC, useState, useEffect, useRef } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Brain, ChevronDown, Sparkles, Maximize2 } from 'lucide-react'
import { cn } from '../../lib/utils'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../ui/collapsible'
import { Button } from '../ui/button'
import { THINKING_THRESHOLDS } from '../../config/thresholds'
import { THINKING_PHASE_SEPARATOR } from '../../utils/thinkingPhase'

interface ThinkingPanelProps {
  thinkingContent?: string
  isStreaming?: boolean
  durationMs?: number
  className?: string
  onExpandToPanel?: () => void
}

/**
 * ThinkingPanel - Displays LLM reasoning/thinking in a collapsible section
 * Clinical Intelligence System design with neural network aesthetic
 */
export const ThinkingPanel: FC<ThinkingPanelProps> = ({
  thinkingContent,
  isStreaming = false,
  durationMs,
  className,
  onExpandToPanel
}) => {
  const [isOpen, setIsOpen] = useState(isStreaming)
  const contentRef = useRef<HTMLDivElement>(null)

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreaming && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight
    }
  }, [thinkingContent, isStreaming])

  // Auto-expand when streaming starts
  useEffect(() => {
    if (isStreaming) {
      setIsOpen(true)
    }
  }, [isStreaming])

  // Check if content should be shown in side panel
  const shouldShowExpandButton = thinkingContent && (
    thinkingContent.length > THINKING_THRESHOLDS.EXPAND_CHARS ||
    (thinkingContent.match(/\n\n/g) || []).length >= THINKING_THRESHOLDS.EXPAND_PARAGRAPHS
  )

  if (!thinkingContent && !isStreaming) return null

  // Reasoning phases joined by THINKING_PHASE_SEPARATOR; rendered as separate blocks below.
  const phases = thinkingContent ? thinkingContent.split(THINKING_PHASE_SEPARATOR) : []

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={className}>
      <div className="flex items-center gap-2">
        <CollapsibleTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-8 px-3 text-xs gap-2 rounded-lg",
              "bg-surface hover:bg-surface-elevated",
              "border border-border/50 hover:border-clinical-teal/30",
              "transition-all duration-200",
              isStreaming && "border-clinical-teal/50 shadow-[0_0_10px_-3px_hsl(var(--clinical-teal)/0.3)]"
            )}
          >
            {/* Neural network icon with animation */}
            <div className="relative">
              <Brain className={cn(
                "h-3.5 w-3.5 text-clinical-teal",
                isStreaming && "ekg-brain"
              )} />
              {isStreaming && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-clinical-cyan opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-clinical-cyan" />
                </span>
              )}
            </div>

            <span className={cn(
              "font-medium",
              isStreaming ? "text-clinical-teal" : "text-muted-foreground"
            )}>
              {isStreaming ? 'Reasoning...' : 'View Reasoning'}
            </span>

            {durationMs && !isStreaming && (
              <span className="text-muted-foreground/60">
                ({formatDuration(durationMs)})
              </span>
            )}

            <ChevronDown className={cn(
              "h-3 w-3 transition-transform text-muted-foreground",
              isOpen && "rotate-180"
            )} />
          </Button>
        </CollapsibleTrigger>

        {/* Expand to side panel button */}
        {shouldShowExpandButton && onExpandToPanel && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 text-muted-foreground hover:text-clinical-teal"
            onClick={onExpandToPanel}
            title="Expand to side panel"
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      <CollapsibleContent className="mt-2 animate-fade-in">
        <div
          ref={contentRef}
          className={cn(
            "relative p-4 rounded-xl",
            "bg-gradient-to-br from-clinical-teal/5 to-clinical-cyan/5",
            "border border-clinical-teal/20",
            "max-h-48 overflow-y-auto",
            "scrollbar-thin scrollbar-thumb-clinical-teal/20 scrollbar-track-transparent"
          )}
        >
          {/* Decorative neural pattern */}
          <div className="absolute top-2 right-2 opacity-10">
            <Sparkles className="h-4 w-4 text-clinical-teal" />
          </div>

          {thinkingContent ? (
            // Split on the known phase separator so multi-phase reasoning renders with a real
            // visual divider between phases instead of a literal "---" (matches the divider the
            // backend's THINKING_PHASE_SEPARATOR was intended to produce). Not full markdown —
            // only this one separator is handled.
            phases.map((phase, i) => (
              <div key={i}>
                {i > 0 && (
                  <hr className="my-3 border-clinical-teal/20" />
                )}
                {/* Markdown (bold, lists, etc.) — a DELIBERATE divergence from the
                    chat bubbles, which render plain pre-wrap text: reasoning often
                    carries structure (lists, emphasis) worth rendering. Note the
                    markdown paragraph rule collapses single newlines to spaces. */}
                <div className={cn(
                  "prose prose-sm dark:prose-invert max-w-none leading-relaxed",
                  // Keep the streaming cursor on the last (active) phase only.
                  isStreaming && i === phases.length - 1 && "thinking-cursor"
                )}>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{phase}</ReactMarkdown>
                </div>
              </div>
            ))
          ) : (
            <p className={cn(
              "text-sm text-muted-foreground leading-relaxed",
              "whitespace-pre-wrap",
              isStreaming && "thinking-cursor"
            )}>
              Processing...
            </p>
          )}

          {/* Streaming indicator */}
          {isStreaming && (
            <div className="absolute bottom-3 right-3">
              <div className="typing-indicator">
                <span />
                <span />
                <span />
              </div>
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  )
}
